/**
 * Transport Booking Page — deep-link enabled.
 *
 * i18n: all visible text is driven by `t()` from the `transport.*`
 * namespace. There are NO inline `{en,et,fi}` label dictionaries.
 */
import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import BookingForm from '@/components/BookingForm';
import { GlobalNav } from '@/components/GlobalNav';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import {
  Truck, MapPin, Package, Home, CheckCircle, Shield, ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const SUBCATEGORY_SLUGS = {
  'kaugvedu': 'long-distance', 'long-distance': 'long-distance',
  'kohalik': 'local', 'local': 'local',
  'kolimine': 'moving', 'moving': 'moving',
  'pakivedu': 'delivery', 'delivery': 'delivery',
};

// Icon + locale-key bindings per subcategory. Label/desc text comes
// from the active locale's `transport.*` dictionary.
const SUBCATEGORY_INFO = {
  'long-distance': { icon: MapPin, labelKey: 'typeLongBookingLabel', descKey: 'typeLongDesc' },
  'local':         { icon: Truck,  labelKey: 'typeLocalLabel',       descKey: 'typeLocalDesc' },
  'moving':        { icon: Home,   labelKey: 'typeMovingLabel',      descKey: 'typeMovingDesc' },
  'delivery':      { icon: Package, labelKey: 'typeDeliveryLabel',   descKey: 'typeDeliveryDesc' },
};

const FEATURE_KEYS = [
  'featureInstantQuotes',
  'featureVerifiedDrivers',
  'featureRealtimeTracking',
  'featureInsuranceIncluded',
];

const TransportBookingPage = () => {
  const { subcategory: subcategorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const tx = (key) => t(`transport.${key}`);

  const subcategoryId = SUBCATEGORY_SLUGS[subcategorySlug] || searchParams.get('subcategory') || null;
  const info = subcategoryId ? SUBCATEGORY_INFO[subcategoryId] : null;
  const Icon = info?.icon || Truck;

  const title = info ? tx(info.labelKey) : tx('bookingTitle');
  const subtitle = info ? tx(info.descKey) : tx('bookingSubtitle');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      <GlobalNav />

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <Link to="/transport" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" />
          {tx('backToTransport')}
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-4 bg-blue-100 text-blue-700">
                <Icon className="h-3.5 w-3.5 mr-1" />
                {tx('badge')}
              </Badge>

              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-testid="transport-booking-title">
                {title}
              </h1>

              <p className="text-lg text-slate-600 max-w-lg" data-testid="transport-booking-subtitle">
                {subtitle}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {FEATURE_KEYS.map((k) => (
                <div key={k} className="flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-sm border text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="font-medium">{tx(k)}</span>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white border-0">
                <CardContent className="py-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Shield className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{tx('trustTitle')}</h3>
                      <p className="text-blue-100 text-sm">{tx('trustDesc')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:sticky lg:top-24"
          >
            <BookingForm
              defaultCategory="transport"
              defaultSubcategory={subcategoryId}
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default TransportBookingPage;
